import { ChangeEvent } from 'react';
import { Field, FieldProps, FormikProps } from 'formik';
import { FormComponent } from '@synergy/abstract';
import { FormControlLabel, Switch, Theme } from '@mui/material';
import { FormattedMessage, FormattedNumber, WrappedComponentProps, injectIntl } from 'react-intl';
import { Locale } from '@synergy/models';
import { PoolTable, PoolTextArea, PoolTextField, ProductDetails } from '@synergy/components';
import { WithStyles, createStyles, withStyles } from '@mui/styles';
import { getCurrencySymbol, noop } from '@synergy/utils';
import urljoin from 'url-join';

import { EditOffer } from '../model/edit-offer';
import { OfferItem } from '../../../../../common/models/offer-item';
import { ROUTES } from '../../../../sidebar-menu/navigation-drawer/routes';

/** Six digits with a separator, at 15px. Only the figures are measured; the
 *  field takes whatever its unit needs, so every unit fits. */
const FIGURE_WIDTH = 56;

/** Above and below the text of a row, the same on either side. */
const CELL_PADDING = 14;
/** The height of a field in this table, as it renders. */
const FIELD_HEIGHT = 29;
/** One line of the text in the cells beside it. */
const CELL_TEXT_LINE = 16;

/**
 * What a cell holding a field keeps above it.
 *
 * A field is taller than one line of text and centres its own line inside
 * itself, so its cell hands back the difference. The text in the field then
 * lands on the line of the text beside it, and every cell keeps equal air above
 * and below its content — derived from the two heights instead of measured per
 * cell (#2216, Sammelpunkt Ausrichtung).
 */
const FIELD_CELL_PADDING_TOP = CELL_PADDING + (CELL_TEXT_LINE - FIELD_HEIGHT) / 2;

const styles = (theme: Theme) =>
    createStyles({
        table: {
            padding: '10px 10px',
            marginBottom: 20,

            /* table head */
            '& thead tr': {
                height: 'auto',
            },
            '& thead tr th': {
                textAlign: 'right',
                padding: '7px 15px 5px 10px',
                margin: 0,
                whiteSpace: 'nowrap',
            },
            '& thead tr th:nth-child(1)': {
                textAlign: 'center',
            },
            '& thead tr th:nth-child(2)': {
                textAlign: 'left',
            },

            /* table body

               One alignment for the row instead of a top padding guessed per
               cell — see the same treatment in the supplier portal (#2216,
               Sammelpunkt Ausrichtung). */
            '& tbody tr td': {
                padding: `${CELL_PADDING}px 15px ${CELL_PADDING}px 10px`,
                textAlign: 'right',
                verticalAlign: 'top',
            },
            '& tbody tr td:nth-child(1)': {
                textAlign: 'center',
            },
            '& tbody tr td:nth-child(2)': {
                textAlign: 'left',
            },
            /* the cells that hold a field */
            '& tbody tr td:nth-child(3), & tbody tr td:nth-child(4), & tbody tr td:nth-child(5)': {
                paddingTop: FIELD_CELL_PADDING_TOP,
            },
        },
        articleName: {
            marginBottom: theme.spacing(),
        },
        button: {
            margin: theme.spacing(),
            marginLeft: 0,
        },
        inputQuantity: {
            /* Fills its cell so every field of a column is the same width: the
           column takes the width of its widest unit, and the figures still line
           up under the heading. */
            width: '100%',
            '& > div > input': {
                textAlign: 'right',
                width: FIGURE_WIDTH,
            },
        },
        /** The unit inside the field, centred on the figure rather than on its leading. */
        unit: {
            fontSize: 12,
            lineHeight: 1,
            color: theme.palette.text.secondary,
            whiteSpace: 'nowrap',
        },
        inputPositionCost: {
            width: 95,
            '& > div > input': {
                textAlign: 'right',
            },
        },
        contentQuantity: {
            fontSize: 12,
        },
        notOffered: {
            opacity: 0.4,
        },
        article: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            paddingBottom: theme.spacing(),
        },
        articleDescription: {
            marginTop: 4,
            color: theme.general.colors.hint,
        },
    });

interface OfferEditFormTableProps extends WithStyles<typeof styles> {
    formikProps: FormikProps<EditOffer>;
    currency: string;
    locale: Locale;
    readonly: boolean;
}

export const OfferEditFormTable = withStyles(styles)(
    injectIntl(
        class OfferEditFormTableBase extends FormComponent<EditOffer, OfferEditFormTableProps & WrappedComponentProps> {
            private columns = [
                { id: 'pos', label: <FormattedMessage id={'app.table.column.positionNumber'} />, sortable: false },
                {
                    id: 'name',
                    label: <FormattedMessage id={'app.table.column.article'} />,
                    sortable: false,
                    width: '100%',
                },
                { id: 'quantity', label: <FormattedMessage id={'app.table.column.quantity'} />, sortable: false },
                {
                    id: 'contentQuantity',
                    label: <FormattedMessage id={'app.table.column.contentQuantity'} />,
                    sortable: false,
                },
                {
                    id: 'price',
                    label: (
                        <FormattedMessage
                            id={'app.table.column.priceWithValue'}
                            values={{ value: getCurrencySymbol(this.props.currency, this.props.locale) }}
                        />
                    ),
                    sortable: false,
                },
                {
                    id: 'positionCost',
                    label: (
                        <FormattedMessage
                            id={'app.table.column.positionValue'}
                            values={{ value: getCurrencySymbol(this.props.currency, this.props.locale) }}
                        />
                    ),
                    sortable: false,
                },
            ];

            private toggleExclusive =
                (props: FormikProps<EditOffer>, mainField: string, excludedField: string) =>
                (event: ChangeEvent<HTMLInputElement>) => {
                    const checked = event.currentTarget.checked;
                    this.setFieldValue(props, mainField)(checked);
                    if (checked) {
                        this.setFieldValue(props, excludedField)(false);
                    }
                };

            private getToggles = (fieldPrefix: string) => {
                return (
                    <>
                        <Field name={`${fieldPrefix}.isOffered`}>
                            {({ field, form }: FieldProps<boolean>) => (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            color="primary"
                                            checked={field.value}
                                            onBlur={field.onBlur}
                                            onChange={this.toggleExclusive(
                                                form,
                                                `${fieldPrefix}.isOffered`,
                                                `${fieldPrefix}.isAlternative`
                                            )}
                                            name={field.name}
                                        />
                                    }
                                    label={<FormattedMessage id="app.inquiries.detail.offerEdit.isOffer" />}
                                />
                            )}
                        </Field>
                        <Field name={`${fieldPrefix}.isAlternative`}>
                            {({ field, form }: FieldProps<boolean>) => (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            color="primary"
                                            checked={field.value}
                                            onBlur={field.onBlur}
                                            onChange={this.toggleExclusive(
                                                form,
                                                `${fieldPrefix}.isAlternative`,
                                                `${fieldPrefix}.isOffered`
                                            )}
                                            name={field.name}
                                        />
                                    }
                                    label={<FormattedMessage id="app.inquiries.detail.offerEdit.isAlternative" />}
                                />
                            )}
                        </Field>
                    </>
                );
            };

            private handlePriceChanged =
                (form: FormikProps<EditOffer>, index: number) => (event: ChangeEvent<HTMLInputElement>) => {
                    // store price
                    const price = +event.target.value;
                    form.setFieldValue(`offerItems.${index}.price`, price);
                    // set contentPrice
                    const contentQuantity = form.values.offerItems[index].contentQuantity;
                    if (contentQuantity && contentQuantity > 0) {
                        form.setFieldValue(`offerItems.${index}.contentQuantityPrice`, price / contentQuantity);
                    }
                };

            private getRow = (offerItem: OfferItem, index: number) => {
                const positionTotal =
                    ((offerItem.isOffered || offerItem.isAlternative) && offerItem.quantity * (offerItem.price || 0)) ||
                    0;
                const contentQuantityPrice =
                    ((offerItem.isOffered || offerItem.isAlternative) && offerItem.contentQuantityPrice) || 0;

                const url = offerItem.product && urljoin(ROUTES.products.product.base, offerItem.product.id.toString());

                return {
                    id: index,
                    pos: index + 1,
                    name: (
                        <>
                            <div
                                className={`${this.props.classes.article} ${
                                    (!offerItem.isOffered && this.props.classes.notOffered) || ''
                                }`}
                            >
                                <ProductDetails product={offerItem} url={url} />
                            </div>
                            {offerItem.isAlternative && (
                                <Field name={`offerItems.${index}.alternativeNote`}>
                                    {({ field, form }: FieldProps<EditOffer>) => (
                                        <PoolTextArea
                                            label={
                                                <FormattedMessage id="app.inquiries.detail.offerEdit.alternativeNote" />
                                            }
                                            className={this.props.classes.articleName}
                                            error={this.hasError(form, field.name)}
                                            helperText={this.getError(form, field.name)}
                                            readOnly={this.props.readonly}
                                            {...field}
                                        />
                                    )}
                                </Field>
                            )}
                            {!this.props.readonly && this.getToggles(`offerItems.${index}`)}
                        </>
                    ),
                    contentQuantity: (
                        <PoolTextField
                            fieldType="dense"
                            className={this.props.classes.inputQuantity}
                            endAdornment={
                                <span className={this.props.classes.unit} title={offerItem.contentUnit}>
                                    {offerItem.contentUnit}
                                </span>
                            }
                            value={
                                (offerItem.contentQuantity &&
                                    this.props.intl.formatNumber(offerItem.contentQuantity, {
                                        style: 'decimal',
                                        maximumFractionDigits: 0,
                                        minimumFractionDigits: 0,
                                    })) ||
                                ''
                            }
                            disabled={!offerItem.isOffered && !offerItem.isAlternative}
                            readOnly={true}
                        />
                    ),
                    quantity: (
                        <PoolTextField
                            fieldType="dense"
                            className={this.props.classes.inputQuantity}
                            endAdornment={
                                <span className={this.props.classes.unit} title={offerItem.orderUnit}>
                                    {offerItem.orderUnit}
                                </span>
                            }
                            value={this.props.intl.formatNumber(offerItem.quantity, {
                                style: 'decimal',
                                maximumFractionDigits: 4,
                                minimumFractionDigits: 0,
                            })}
                            disabled={!offerItem.isOffered && !offerItem.isAlternative}
                            readOnly={true}
                        />
                    ),
                    price: (
                        <Field name={`offerItems.${index}.price`}>
                            {({ field, form }: FieldProps<EditOffer>) => (
                                <PoolTextField
                                    fieldType="dense"
                                    className={this.props.classes.inputPositionCost}
                                    disabled={!offerItem.isOffered && !offerItem.isAlternative}
                                    error={this.hasError(form, field.name)}
                                    helperText={this.getError(form, field.name)}
                                    {...field}
                                    onChange={this.handlePriceChanged(form, index)}
                                    readOnly={this.props.readonly}
                                    isNumeric={true}
                                />
                            )}
                        </Field>
                    ),
                    positionCost: (
                        <>
                            <div>
                                <FormattedNumber
                                    currency={this.props.currency}
                                    value={positionTotal}
                                    style="currency"
                                />
                            </div>
                            {offerItem.contentQuantityPrice && (
                                <div className={this.props.classes.contentQuantity}>
                                    <FormattedNumber
                                        currency={this.props.currency}
                                        value={contentQuantityPrice}
                                        style="currency"
                                    />
                                    &nbsp;/&nbsp;
                                    {offerItem.contentUnit}
                                </div>
                            )}
                        </>
                    ),
                };
            };

            public render() {
                const { classes } = this.props;
                const rows = this.props.formikProps.values.offerItems.map(this.getRow);

                return (
                    <div>
                        <PoolTable
                            enableHover={false}
                            columns={this.columns}
                            rows={rows}
                            onRowClicked={noop}
                            className={classes.table}
                        />
                    </div>
                );
            }
        }
    )
);
